import { addAreaValueDataObjects, AreaValue, subtractAreaValueDataObjects, type AreaValueDataObject } from '../area'
import { addMonetaryValues, MonetaryValue, subtractMonetaryValues, type MonetaryValueDataObjectType } from '../money'
import { isParkingType, type TenantUsageTypeLabelKeys } from '../tenant'
import type { TenantSummaryBreakdown, TenantSummarySums } from '../../widgets/TenantTables/TenantSummaryTables/types'

type ImportedTenant = {
    usage_type: TenantUsageTypeLabelKeys
    rental_status: 'rented' | 'unrented' | 'self_used'
    number_of_units: number
    area?: AreaValueDataObject
    rent_details: { yearly_rent: MonetaryValueDataObjectType }
}

type ImportedTenantWithMarketRent = ImportedTenant & {
    market_rent_details?: { yearly_rent: MonetaryValueDataObjectType }
}

type SummarisableTenant = ImportedTenantWithMarketRent & {
    usage_type: Exclude<TenantUsageTypeLabelKeys, 'total'>
}

export type ImportedTenantSummaryItem = {
    type: Exclude<TenantUsageTypeLabelKeys, 'total'>
    number_of_units: number
    vacancy_number_of_units: number
    total_area: AreaValueDataObject
    vacancy_area: AreaValueDataObject
    actual_rental_income: MonetaryValueDataObjectType
    gross_rental_income: MonetaryValueDataObjectType
    net_rental_income: MonetaryValueDataObjectType
    management_cost: MonetaryValueDataObjectType
    administrative_cost: MonetaryValueDataObjectType
    maintenance_cost: MonetaryValueDataObjectType
    operating_cost: MonetaryValueDataObjectType
    loss_of_rent_risk_cost: MonetaryValueDataObjectType
    discount_rate: number
}

export function getTenantSummaryFromDetails(tenants: SummarisableTenant[]): TenantSummarySums {
    return tenants.reduce<TenantSummarySums>(
        (summary, tenant) => {
            const isParking = isParkingType(tenant.usage_type)
            const isVacant = tenant.rental_status === 'unrented'
            return {
                number_of_units: summary.number_of_units + (isParking ? 0 : tenant.number_of_units),
                vacancy_number_of_units: summary.vacancy_number_of_units + (isParking || !isVacant ? 0 : tenant.number_of_units),
                parking_units: summary.parking_units + (isParking ? tenant.number_of_units : 0),
                vacant_parking_units: summary.vacant_parking_units + (isParking && isVacant ? tenant.number_of_units : 0),
                total_area: addAreaValueDataObjects(summary.total_area, tenant.area ?? new AreaValue(0)),
                vacancy_area: addAreaValueDataObjects(summary.vacancy_area, isVacant && tenant.area ? tenant.area : new AreaValue(0)),
                actual_rental_income: addMonetaryValues(summary.actual_rental_income, tenant.rent_details.yearly_rent),
                gross_rental_income: addMonetaryValues(summary.gross_rental_income, tenant.market_rent_details?.yearly_rent),
            }
        },
        {
            number_of_units: 0,
            vacancy_number_of_units: 0,
            parking_units: 0,
            vacant_parking_units: 0,
            total_area: new AreaValue(0),
            vacancy_area: new AreaValue(0),
            actual_rental_income: MonetaryValue.zero(),
            gross_rental_income: MonetaryValue.zero(),
        }
    )
}

export function getTenantSummarySums(items: readonly TenantSummaryBreakdown[]): TenantSummarySums {
    return items.reduce<TenantSummarySums>(
        (summary, item) => {
            const isParking = isParkingType(item.type)
            return {
                number_of_units: summary.number_of_units + (isParking ? 0 : item.number_of_units),
                vacancy_number_of_units: summary.vacancy_number_of_units + (isParking ? 0 : item.vacancy_number_of_units),
                parking_units: summary.parking_units + (isParking ? item.number_of_units : 0),
                vacant_parking_units: summary.vacant_parking_units + (isParking ? item.vacancy_number_of_units : 0),
                total_area: addAreaValueDataObjects(summary.total_area, item.total_area),
                vacancy_area: addAreaValueDataObjects(summary.vacancy_area, item.vacancy_area),
                actual_rental_income: addMonetaryValues(summary.actual_rental_income, item.actual_rental_income),
                gross_rental_income: addMonetaryValues(summary.gross_rental_income, item.gross_rental_income),
            }
        },
        {
            number_of_units: 0,
            vacancy_number_of_units: 0,
            parking_units: 0,
            vacant_parking_units: 0,
            total_area: new AreaValue(0),
            vacancy_area: new AreaValue(0),
            actual_rental_income: MonetaryValue.zero(),
            gross_rental_income: MonetaryValue.zero(),
        }
    )
}

export function getTenantSummaryTableRows<TItem extends TenantSummaryBreakdown>(
    summaryItems: readonly TItem[],
    tenants: readonly SummarisableTenant[]
): Array<TItem & { calculatedSummary: TenantSummarySums; calculatedDiff: Partial<TenantSummaryBreakdown> }> {
    return summaryItems.map((summaryItem) => {
        const matchingTenants = tenants.filter((tenant) => tenant.usage_type === summaryItem.type)
        const calculatedSummary = getTenantSummaryFromDetails(matchingTenants)
        const isParking = isParkingType(summaryItem.type)
        const calculatedDiff: Partial<TenantSummaryBreakdown> = matchingTenants.length
            ? {
                  number_of_units:
                      summaryItem.number_of_units - (isParking ? calculatedSummary.parking_units : calculatedSummary.number_of_units),
                  vacancy_number_of_units:
                      summaryItem.vacancy_number_of_units -
                      (isParking ? calculatedSummary.vacant_parking_units : calculatedSummary.vacancy_number_of_units),
                  total_area: subtractAreaValueDataObjects(summaryItem.total_area, calculatedSummary.total_area),
                  vacancy_area: subtractAreaValueDataObjects(summaryItem.vacancy_area, calculatedSummary.vacancy_area),
                  actual_rental_income: subtractMonetaryValues(summaryItem.actual_rental_income, calculatedSummary.actual_rental_income),
                  gross_rental_income: subtractMonetaryValues(summaryItem.gross_rental_income, calculatedSummary.gross_rental_income),
              }
            : {}
        return { ...summaryItem, calculatedSummary, calculatedDiff }
    })
}

export function buildTenantSummaryTableItemsFromDetails(tenants: SummarisableTenant[]): ImportedTenantSummaryItem[] {
    return [...new Set(tenants.map((tenant) => tenant.usage_type))].map((usageType) => {
        const summary = getTenantSummaryFromDetails(tenants.filter((tenant) => tenant.usage_type === usageType))
        const isParking = isParkingType(usageType)
        return {
            type: usageType,
            number_of_units: isParking ? summary.parking_units : summary.number_of_units,
            vacancy_number_of_units: isParking ? summary.vacant_parking_units : summary.vacancy_number_of_units,
            total_area: summary.total_area,
            vacancy_area: summary.vacancy_area,
            actual_rental_income: summary.actual_rental_income,
            gross_rental_income: summary.gross_rental_income,
            net_rental_income: MonetaryValue.zero(),
            management_cost: MonetaryValue.zero(),
            administrative_cost: MonetaryValue.zero(),
            maintenance_cost: MonetaryValue.zero(),
            operating_cost: MonetaryValue.zero(),
            loss_of_rent_risk_cost: MonetaryValue.zero(),
            discount_rate: 0,
        }
    })
}
